import {
  MetricsBox,
  RatioProgressBar,
  Skeleton,
  Stack,
  tokens,
} from '@cast/design-system';
import { FailedToLoad } from '@cast/shared-messages-ui';

import { WidgetLayout } from '../../../_components/ui/WidgetLayout';

type Metric = {
  label: string;
  value: number;
  unit?: string;
  color: string;
  testId: string;
};

export type MetricsProgressCardProps = {
  title: string;
  metrics: Metric[];
  testId: string;
  isLoading?: boolean;
  isFetching?: boolean;
  error?: unknown;
  refresh?: () => void;
};

const MetricsProgressCardSkeleton = ({ testId }: { testId: string }) => (
  <Stack gap={24} height="100%" justifyContent="space-between">
    <Stack direction="row" gap={16}>
      {Array.from({ length: 3 }).map((_, index) => (
        <MetricsBox
          key={index}
          testId={`${testId}-skeleton-${index}`}
          size="m"
          slots={{
            startContent: (
              <Skeleton
                variant="rectangular"
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: tokens.theme.radius.xs,
                }}
              />
            ),
            label: <Skeleton variant="text" width={60} />,
            value: <Skeleton variant="text" width={40} />,
            unit: <Skeleton variant="text" width={50} />,
          }}
        />
      ))}
    </Stack>
    <Skeleton
      variant="rectangular"
      height={16}
      width="100%"
      sx={{ borderRadius: tokens.theme.radius.xs }}
    />
  </Stack>
);

export const MetricsProgressCard = ({
  title,
  metrics,
  testId,
  isLoading,
  isFetching,
  error,
  refresh,
}: MetricsProgressCardProps) => {
  const hasError = !!error;
  const isCardLoading = isLoading || (hasError && isFetching);

  if (hasError && !isFetching) {
    return (
      <WidgetLayout title={title} testId={testId} headerDivider={false}>
        <FailedToLoad
          title={`Failed to load ${title.toLowerCase()}`}
          refresh={refresh}
        />
      </WidgetLayout>
    );
  }

  return (
    <WidgetLayout title={title} testId={testId} headerDivider={false}>
      {isCardLoading ? (
        <MetricsProgressCardSkeleton testId={testId} />
      ) : (
        <Stack gap={24} height="100%" justifyContent="space-between">
          <Stack direction="row" gap={16}>
            {metrics.map((metric) => (
              <MetricsBox
                key={metric.label}
                testId={metric.testId}
                size="m"
                slots={{
                  startContent: (
                    <Stack
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: tokens.theme.radius.xs,
                        backgroundColor: metric.color,
                      }}
                    />
                  ),
                  label: metric.label.toUpperCase(),
                  value: `${metric.value}`,
                  unit: metric.unit,
                }}
                slotProps={{
                  label: { variant: 'L10M', color: 'textSecondary' },
                  value: { variant: 'metricmd' },
                  unit: { variant: 'L10R', color: 'textSecondary' },
                }}
              />
            ))}
          </Stack>
          <RatioProgressBar
            testId={`${testId}-bar`}
            hideBorders
            bars={metrics.map((metric) => ({
              value: metric.value,
              color: metric.color,
            }))}
            total={100}
          />
        </Stack>
      )}
    </WidgetLayout>
  );
};
